import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  BasicMetaData, NearbyOrderModel,
  Order,
  OrderFilterModel,
  OrderModel,
  OrderModelExpectBackend,
  OrdersCountModel,
  OrderStatusExpectBackend,
  Sort,
  TYPE_SEARCH
} from 'shared';
import { PaginateQuery, PaginatorModel, UUID } from 'symbols';


@Injectable()
export class OrdersService {
  public sortDefault$ = new BehaviorSubject({ sort: Sort.CREATED, order: Order.DESC });

  private readonly apiUrl = '/api/orders';

  constructor(private http: HttpClient) {
  }

  getOrderList(filter?: PaginateQuery<OrderFilterModel>): Observable<PaginatorModel<OrderModel>> {
    const params: { [key: string]: any } = {
      page: filter?.page || 1,
      limit: filter?.limit || 50,
      // усі правила сортування (напр. спершу пріоритетні, потім за датою), а не лише перше
      sortBy: (filter?.sortBy || []).map(([column, order]) => `${column}:${order}`),
      typeSearch: filter?.filter?.typeSearch || TYPE_SEARCH.BY_METADATA,
      filter: filter?.filter
    };

    if (filter?.filter?.startDate) {
      params['filter.startDate'] = filter?.filter?.startDate;
    }

    if (filter?.filter?.typeSearch === TYPE_SEARCH.BY_NUMBER) {
      params['filter.docNumber'] = +filter?.filter?.searchText || '';
    } else {
      params['search'] = filter?.filter?.searchText || '';
    }

    if (filter?.filter?.isToxic) {
      params['filter.isToxic'] = filter?.filter?.isToxic;
    }

    if (filter?.filter?.isDebt) {
      params['filter.isDebt'] = filter?.filter?.isDebt;
    }

    if (filter?.filter?.isHighPriority) {
      params['filter.isHighPriority'] = filter?.filter?.isHighPriority;
    }

    if (filter?.filter?.masterId) {
      params['filter.masterId'] = filter?.filter?.masterId;
    }

    if (filter?.filter?.statusIds?.length) {
      params['filter.statusIds'] = filter?.filter?.statusIds;
    }
    return this.http.get<PaginatorModel<OrderModel>>(this.apiUrl, { params });
  }


  getOrderListLazy(url: string): Observable<PaginatorModel<OrderModel>> {
    return this.http.get<PaginatorModel<OrderModel>>(url);
  }

  getStatusList(): Observable<OrderStatusExpectBackend[]> {
    return this.http.get(`${this.apiUrl}/status-list`) as Observable<OrderStatusExpectBackend[]>;
  }

  getOrderById(id: string): Observable<OrderModel> {
    return this.http.get<OrderModel>(`${this.apiUrl}/order/${id}`);
  }

  createOrder(order: OrderModelExpectBackend): Observable<OrderModel> {
    return this.http.post<OrderModel>(`${this.apiUrl}/`, { ...order });
  }

  updateOrder(id: string, order: OrderModelExpectBackend, force: boolean): Observable<OrderModel> {
    return this.http.patch<OrderModel>(`${this.apiUrl}/${id}`, { ...order, force });
  }

  getNextMetaData(): Observable<BasicMetaData> {
    return this.http.get<BasicMetaData>(`${this.apiUrl}/next-meta-data`);
  }

  getOrdersCount(): Observable<OrdersCountModel> {
    return this.http.get<OrdersCountModel>(`${this.apiUrl}/accepted-orders-count`);
  }

  issuedOrder(orderId: string): Observable<OrderModel> {
    return this.http.post<OrderModel>(`${this.apiUrl}/issued-order/${orderId}`, {});
  }

  setNotConfirmedOrder(orderId: UUID): Observable<OrderModel> {
    return this.http.post<OrderModel>(`${this.apiUrl}/not-confirmed/${orderId}`, {});
  }

  getNearbyOrders(id: string, limit = 5): Observable<NearbyOrderModel[]> {
    return this.http.get<NearbyOrderModel[]>(`${this.apiUrl}/${id}/nearby`, { params: { limit } });
  }

  getNearbyOrdersByLocation(lat: number, lng: number, limit = 5): Observable<NearbyOrderModel[]> {
    return this.http.get<NearbyOrderModel[]>(`${this.apiUrl}/nearby-by-location`, { params: { lat, lng, limit } });
  }

  getAiSuggestion(id: string): Observable<{ suggestion: string }> {
    return this.http.get<{ suggestion: string }>(`${this.apiUrl}/${id}/ai-suggest`);
  }
}
